// The public reader of the season chronicle (chronicle.md §9.4, R1181).
//
// A visitor opens `/?chronicle=<publicId>.<world>.<season>` and reads one
// shared closed volume. The page needs no session: `fast/main.ts` shows
// the reader before the entry resolves, so the landing screen, the hub and
// the world socket never start. The reader shows the pages the owner sees,
// with no share switch, no COPY LINK, no volume bar and no REPLAY link.
//
// The seed of the variant pick is the public id of the owner, in the book
// of the owner and here (chronicle.md §4.1). The two therefore read the same
// words. The public read answers 404 for a volume that is not shared, is
// live or does not exist, and the reader shows the one "not shared" state
// for all three (R1168).
//
// The reader wears the hub's stylesheet: its root is `#hub`, and the game
// page already loads `hub.css` through `hub.ts`.
//
// A volume whose season stored a timelapse opens with THE SEA card of the
// timelapse (card 10 of mockup round 076, R1620), and each day page
// carries ▸ ON THE MAP. The reader reads the frames from the public route
// beside the pages; a refusal or a byte string that does not decode shows
// no card, and the pages read as before.

import { linkButton } from '../../choice/choice';
import {
  ApiError, STATUS_CHRONICLE_NOT_SHARED, getSharedChronicle, getSharedTimelapse,
  type ChronicleVolumePages,
} from '../../../net/api';
import { decodeTimelapse, type Timelapse } from '../../../net/timelapse';
import { ListTap } from '../../touch';
import { ReaderTimelapse, onTheMapButton } from '../../timelapse/reader';
import { chronicleNoun } from '../../../gen/nouns';
import { chronicle as C } from '../../../gen/strings';
import { card, empty, esc, failed, h1, lede, loading, page } from '../panel';
import { dayCard, dayViews, seasonCard } from './book';
import { pageBase, type ReaderAddress } from './address';
import { renderSeason, type ChronicleScope } from './render';

/** What the reader holds: the read in flight, the volume and its season
 *  timelapse (null for none), or the refusal. */
export type ReaderState =
  | { kind: 'loading' }
  | { kind: 'ready'; volume: ChronicleVolumePages; timelapse?: Timelapse | null }
  | { kind: 'failed'; error: unknown };

/** The place of THE SEA card at the head of the season page (R1620). */
const TIMELAPSE_HOST = '<div class="tlhost"></div>';

/** The seed scope of a public read. It is the scope the owner's book
 *  builds (`book.ts scopeOf`): the owner's public id, the world and the
 *  season. */
export function readerScope(a: ReaderAddress, v: ChronicleVolumePages): ChronicleScope {
  return { account: a.publicId, world: v.world, season: v.season, worldName: v.worldName };
}

/** The plain title of a volume: the commander and the world. */
export function readerTitle(v: ChronicleVolumePages): string {
  return v.commander === undefined
    ? C.reader_anon({ world: v.worldName })
    : C.reader_title({ commander: v.commander, world: v.worldName });
}

/** True for the refusal that means "not shared". */
function notShared(err: unknown): boolean {
  return err instanceof ApiError && err.status === STATUS_CHRONICLE_NOT_SHARED;
}

/** The whole reader as markup. Pure, so a test reads it with no DOM. */
export function readerHtml(a: ReaderAddress, s: ReaderState, base = pageBase()): string {
  const home = `<div class="chronbar">${linkButton(C.reader_home, base, { variant: 'primary' })}</div>`;
  if (s.kind === 'loading') return page(h1(C.book_title), card(C.book_title, '▤', '', loading()), home);
  if (s.kind === 'failed') {
    const body = notShared(s.error) ? empty(C.not_shared, C.not_shared_why) : failed(s.error);
    return page(h1(C.book_title), card(C.book_title, '▤', '', body), home);
  }
  const v = s.volume;
  const scope = readerScope(a, v);
  const days = dayViews(v, scope);
  const seasonPage = v.pages.find((p) => p.day === 0);
  const hasSea = (s.timelapse ?? null) !== null;
  let out = `<div class="chronshare"><span class="tag">${esc(C.closed_tag)}</span>`
    + `${lede(C.reader_what)}</div>`;
  // THE SEA card heads the season page; the page mounts it here (R1620).
  if (hasSea) out += TIMELAPSE_HOST;
  if (seasonPage) out += seasonCard(renderSeason(seasonPage, days, scope, C, chronicleNoun));
  // No REPLAY link: a shared volume is closed, and the roll deleted its
  // combat log (R1169).
  out += days.map((d) => dayCard(d, 0, hasSea ? onTheMapButton(d.day) : '')).join('');
  if (!seasonPage && days.length === 0) out += card(C.book_title, '▤', '', empty(C.no_page));
  return page(h1(readerTitle(v), C.volume_label({ season: v.season, world: v.worldName })), out, home);
}

/** Shows the reader of one volume. It never resolves: the reader is the
 *  whole page, and a link leaves it. */
export function showReader(
  a: ReaderAddress,
  read: (publicId: string, world: number, season: number) => Promise<ChronicleVolumePages> = getSharedChronicle,
  readFrames: (publicId: string, world: number, season: number) => Promise<Uint8Array> = getSharedTimelapse,
): void {
  const root = document.createElement('div');
  root.id = 'hub';
  root.className = 'reader';
  const pane = document.createElement('div');
  pane.className = 'pane';
  const inner = document.createElement('div');
  inner.className = 'paneinner';
  pane.append(inner);
  root.append(pane);
  document.body.appendChild(root);
  let sea: ReaderTimelapse | null = null;
  /** ▸ ON THE MAP: the day of the press, on THE SEA card. */
  const showDay = (target: Element | null | undefined): void => {
    const btn = target?.closest?.('[data-tl="day"]');
    if (btn instanceof HTMLElement) sea?.showDay(Number(btn.dataset.day));
  };
  const draw = (s: ReaderState): void => {
    inner.innerHTML = readerHtml(a, s);
    document.title = s.kind === 'ready' ? readerTitle(s.volume) : C.book_title;
    sea = null;
    if (s.kind !== 'ready' || !s.timelapse) return;
    const host = inner.querySelector('.tlhost');
    if (!host) return;
    sea = new ReaderTimelapse(s.timelapse, s.volume.faction ?? 0, s.volume.commander ?? '');
    host.replaceWith(sea.el);
    sea.draw();
  };
  // ▸ ON THE MAP stands in a pane that scrolls under the same finger, so
  // it acts on the release (§16.8, R1206).
  const tap = new ListTap<HTMLElement>();
  tap.onTap = showDay;
  pane.addEventListener('pointerdown', (ev) => {
    if (ev.button > 0) return;
    tap.down(ev.pointerId, ev.clientX, ev.clientY, ev.target as HTMLElement);
  });
  pane.addEventListener('pointermove', (ev) => tap.move(ev.pointerId, ev.clientX, ev.clientY));
  pane.addEventListener('pointerup', (ev) => tap.up(ev.pointerId, ev.clientX, ev.clientY));
  pane.addEventListener('pointercancel', () => tap.cancel());
  pane.addEventListener('click', (ev) => {
    if (ev.detail === 0) showDay(ev.target as Element | null);
  });
  draw({ kind: 'loading' });
  // The frames are read beside the pages. A refusal (a season that stored
  // none answers 404) and a byte string that does not decode both mean no
  // card (chronicle.md §9.4).
  const frames = readFrames(a.publicId, a.world, a.season)
    .then((b) => decodeTimelapse(b))
    .catch(() => null)
    .then((t) => (t !== null && t.frames.length > 0 ? t : null));
  read(a.publicId, a.world, a.season).then(
    async (volume) => draw({ kind: 'ready', volume, timelapse: await frames }),
    (error: unknown) => draw({ kind: 'failed', error }),
  );
}
